import React, { memo, forwardRef, useRef, useMemo, useImperativeHandle, useContext, useState, useCallback, useEffect, Component } from 'react';
import { Link } from '../../components/Generic';
import { Loading } from '../../components/Control';
import { EmpAvatar } from '../../components/EmpUtils';
import { ColGroup } from '../../components/Utils';
import { Tip } from '../../components/Dialogs';
import { useBooleanFlag, useForceUpdate } from '../../components/Hooks';
import { ShiftingContext } from './ShiftingContext';
import { Checkbox } from '../../components/Input';
import { useGridData } from './ShiftingSidePanelHooks';
import _ from "lodash";

const { adapt } = LocaleUtils;
const { getLabel } = OptionUtils;
const { dateTime, date } = Utils.Format;
const ISO_FMT = 'YYYY-MM-DD';


export const ShiftingSidePanel = ({ children, ...props }) => {
	const [currentTab, setCurrentTab] = useState(0);

	const element = [];
	const tabElement = [];
	const contentElement = [];
	let tabIndex = 0;
	React.Children.forEach(children, (child, index) => {
		if (child?.type?.name === 'ShiftingSidePanelTab') {
			let _tabIndex = tabIndex;
			tabElement.push(
				<div
					className={`-tab${tabIndex === currentTab ? ' -active' : ''}`}
					key={_tabIndex}
					onClick={() => {
						setCurrentTab(_tabIndex);
					}
				}>
					{child?.props.name}
				</div>
			);
			if (tabIndex === currentTab) {
				contentElement.push(child);
			}
			tabIndex++;

		} else {
			element.push(child);
		}
	});

	return <div className='H2-ShiftingSidePanel' { ...props }>
		{element}
		{tabElement.length < 2 ? null : <div className='-tabs'>
			{tabElement}
		</div>}
		{contentElement}
	</div>;
};

export const ShiftingSidePanelTab = ({ children, name }) => {
	return <>
		{/* <div className='-tab' onClick={onClick}>{name}</div> */}
		{children}
	</>;
};

export const SummarySection = ({
	data: aftSummary,
	calendarLeaves: calendarLeaveMap,
}) => {
	const { data } = useGridData(aftSummary, undefined, calendarLeaveMap);
	return <>
		<div className='-desc'>{getLabel('h2.am.ShiftingSidePanelComps', 'workingHoursStatistics')}</div>
		<table className="-shift-side-table">
			<ColGroup cols={[120, '*', '*', '*', '*']} />
			<thead>
				<tr>
					<th>{getLabel('h2.am.ShiftingSidePanelComps', 'project')}</th>
					<th>{getLabel('h2.am.ShiftingSidePanelComps', 'shouldBeScheduled')}</th>
					<th>{getLabel('h2.am.ShiftingSidePanelComps', 'alreadyScheduled')}</th>
					<th>{getLabel('h2.am.ShiftingSidePanelComps', 'difference')}</th>
				</tr>
			</thead>
			<tbody>
				{_.map(data, rowData => <tr key={rowData.key}>
					<td>{rowData.name}</td>
					<td>{rowData.should}</td>
					<td>{rowData.aft || 0}</td>
					<td>{rowData.shouldDiff}</td>
				</tr>)}
			</tbody>
		</table>
	</>;
};

export const WorkHourRangeSection = ({
	workHourRangeJson
}) => {
	const [hover, setHover] = useState(null);
	const _dom = useRef({});

	return (
		<>
			<div className="-desc">{getLabel('h2.am.ShiftingSidePanelComps', 'statisticsOfFlexibleWorkingHoursIntervals')}</div>
			<table className="-shift-side-table">
				<tbody>
					<tr>
						<th>{getLabel('h2.am.ShiftingSidePanelComps', 'period')}</th>
						<th>{getLabel('h2.am.ShiftingSidePanelComps', 'workingHours')}</th>
						<th>{getLabel('h2.am.ShiftingSidePanelComps', 'publicHolidays')}</th>
						<th>{getLabel('h2.am.ShiftingSidePanelComps', 'restDays')}</th>
					</tr>
					{(workHourRangeJson || []).map((val, idx) => {
						const obj = val.calendarJson.reduce((a, v) => ({ ...a, [v.id]: v.num }), {});
						const range = val.workHourRange.split('-');
						return <tr key={idx}>
							<td ref={ele => _dom.current['work' + idx] = ele} {...!val.full && {
								onMouseEnter: () => setHover(idx),
								onMouseLeave: () => setHover(null),
							}}>
								<p>
									{range[0]}-
								</p>
								<p>{range[1]}</p>
								{hover != null && hover == idx && <Tip {...{
									open: true, stick: _dom.current['work' + idx], wrapperStyle: { maxWidth: 300 },
								}}>{getLabel('h2.am.ShiftingSidePanelComps', 'theCycleDaysAreNotMet')}</Tip>}
							</td>
							<td>{Number(val.workHours || 0)} {getLabel('h2.am.ShiftingSidePanelComps', 'hour')}{_.isEqual(user.locale, 'en_US') && (Number(val.workHours || 0)) > 1? 's': ''}</td>
							<td>{Number(obj[4] || 0)} {getLabel('h2.am.ShiftingSidePanelComps', 'day')}{_.isEqual(user.locale, 'en_US') && (Number(obj[4] || 0)) > 1? 's': ''}</td>
							<td>{Number(obj[3] || 0)} {getLabel('h2.am.ShiftingSidePanelComps', 'day')}{_.isEqual(user.locale, 'en_US') && (Number(obj[3] || 0)) > 1? 's': ''}</td>
						</tr>;
					})}
				</tbody>
			</table>
		</>
	);
};

export const CalendarSummary = ({ calendarJson, calendarLeaves }) => {
	return (
		<>
			<div className="-desc">{getLabel('h2.am.ShiftingSidePanelComps', 'calendarStatistics')}</div>
			<table className="-shift-side-table">
				<tbody>
					<tr><th>{getLabel('h2.am.ShiftingSidePanelComps', 'calendar')}</th><th> {getLabel('h2.am.ShiftingSidePanelComps', 'days')}</th></tr>
					{calendarJson && calendarJson.map((val, idx) => {
						const obj = calendarLeaves.find(o => val.id == o.id);
						return obj && (+val.num > 0 || obj.active == 1) && <tr key={idx}>
							<td>{adapt(obj.name)}</td><td>{+val.num || 0} {getLabel('h2.am.ShiftingSidePanelComps', 'day')}{_.isEqual(user.locale, 'en_US') && (+val.num || 0) > 1 ? 's': ''}</td>
						</tr>;
					})}
				</tbody>
			</table>
		</>
	);
};

export const ShiftSummary = ({ shiftJson, shifts, shiftJsonWeekday }) => {
	const DAYS = [
		getLabel('global.normal','weekToNum7'), getLabel('global.normal','weekToNum1'), getLabel('global.normal','weekToNum2'), getLabel('global.normal','weekToNum3'), 
		getLabel('global.normal','weekToNum4'), getLabel('global.normal','weekToNum5'), getLabel('global.normal','weekToNum6')
	];
	const [classCountFlag, setClassCountFlag] = useState(1);
	const [renderCheckBox, setRenderCheckBox] = useState(Date.now());

	useEffect(() => {
		new H2.Ajax({
			url: `/api/global/loadUserProp/${'ShiftingSidePanel'}/${'classCountWorkingDay'}`,
			method: 'get',
			onSuccess: (data) => {
				if (data?.classCountFlag !== undefined){
					const { classCountFlag } = data;
					setClassCountFlag(classCountFlag);
				}
				setRenderCheckBox(Date.now());
			},
			onError: () => setRenderCheckBox(Date.now()),
		}).send();
	}, [shiftJson, shifts, shiftJsonWeekday]);

	const handleCheckboxUpdate = (val) => {
		setClassCountFlag(val);
		new H2.Ajax({
			url: `/api/global/saveUserProp`,
			method: 'post',
			data: {
				condCode: 'classCountWorkingDay',
				funCode: 'ShiftingSidePanel',
				value: {'classCountFlag': val}
			},
		}).send();
	};

	const _tipWork = (r, i) => <>
		{!!i && '、'}{r.weekdays.map(w => DAYS[0][w]).join('、')}
		{r.weekdays.length > 0 && '：'}
		{getLabel('am.base.dayRange', r.sdate)} {moment(r.s).format('HH:mm')} - <br />
		{getLabel('am.base.dayRange', r.edate)} {moment(r.e).format('HH:mm')}
	</>;

	const renderRow = (val, idx, shift) => {
		let workList = [];
		if (shift.worktimeSetting === 0) {
			//固定
			const { s, e } = shift.worktime[0];
			const sdate = s.split(/\s+/i)[0];
			const edate = e.split(/\s+/i)[0];
			workList.push({ weekdays: [], s, e, sdate, edate });
		} else if (shift.worktimeSetting === 1) {
			//逐日
			let wMap = {};
			shift.worktime = _.sortBy(shift.worktime, o => o.weekday === 0 ? 99 : o.weekday);
			Object.values(shift.worktime).map(v => {
				const { s, e, weekday } = v;
				if (!wMap[`${s} ${e}`]) {
					wMap[`${s} ${v.e}`] = {
						weekdays: [weekday], s, e, weekday,
						sdate: s.split(/\s+/i)[0], edate: e.split(/\s+/i)[0],
					};
				} else if (!wMap[`${v.s} ${v.e}`].weekdays.includes(weekday)) {
					wMap[`${s} ${e}`].weekdays.push(weekday);
				}
			});
			workList = Object.values(wMap);
		}

		return <tr key={idx}>
			<td style={{ maxWidth: 120 }}>{adapt(shift.abbrName)}</td>
			<td>{+val.num || 0} {getLabel('h2.am.ShiftingSidePanelComps','day')}{_.isEqual(user.locale, 'en_US') && (+val.num || 0) > 1 ? 's': ''}</td>
		</tr>;
	};

	return (
		<>
			<div>
				<span className="-desc">
					{getLabel('h2.am.ShiftingSidePanelComps','shiftStatistics')}
				</span>
				<span style={{display: 'inline-block', marginLeft: '16px', marginBottom: '8px'}}>
					<Checkbox {...{
						key: `ShiftSummary_${renderCheckBox}`,
						validate: { required: false },
						data: { 1: getLabel('h2.am.ShiftingSidePanelComps','onlyWorkingDays') },
						initValue: classCountFlag,
						binary: true,
						onUpdate: handleCheckboxUpdate,
					}} />
				</span>
			</div>
			<table className="-shift-side-table">
				<tbody>
					<tr>
						<th>{getLabel('h2.am.ShiftingSidePanelComps','shift')}</th>
						{/* <th style={{ maxWidth: 100 }}>工作時間</th> */}
						<th>{getLabel('h2.am.ShiftingSidePanelComps','days')}</th>
					</tr>
					{ classCountFlag === 1 ? 
						shiftJsonWeekday && shiftJsonWeekday.map((val, idx) => {
							const shift = shifts.find(o => val.id === o.id) || { id: 0, name: '' };
							return renderRow(val, idx, shift);
						})
						: 
						shiftJson && shiftJson.map((val, idx) => {
							const shift = shifts.find(o => val.id === o.id) || { id: 0, name: '' };
							return renderRow(val, idx, shift);
						})
					}
				</tbody>
			</table>
		</>
	);
};

export const WorkLabelSummary = ({ workLabelJson, workLabels, workLabelJsonWeekday }) => {
	const [shiftCountFlag, setShiftCountFlag] = useState(1);
	const [renderCheckBox, setRenderCheckBox] = useState(Date.now());

	useEffect(() => {
		new H2.Ajax({
			url: `/api/global/loadUserProp/${'ShiftingSidePanel'}/${'shiftCountWorkingDay'}`,
			method: 'get',
			onSuccess: (data) => {	
				if (data?.shiftCountFlag !== undefined){
					const { shiftCountFlag } = data;
					setShiftCountFlag(shiftCountFlag);
				}
				setRenderCheckBox(Date.now());
			},
			onError: () => setRenderCheckBox(Date.now()),
		}).send();
	}, [workLabelJson, workLabels, workLabelJsonWeekday]);

	const handleCheckboxUpdate = (val) => {
		setShiftCountFlag(val);
		new H2.Ajax({
			url: `/api/global/saveUserProp`,
			method: 'post',
			data: {
				condCode: 'shiftCountWorkingDay',
				funCode: 'ShiftingSidePanel',
				value: {'shiftCountFlag': val}
			},
		}).send();
	};
	return <>
		<div>
			<span className="-desc">
				{getLabel('h2.am.ShiftingSidePanelComps','labelStatistics')}
			</span>
			<span style={{display: 'inline-block', marginLeft: '16px', marginBottom: '8px'}}>
				<Checkbox {...{
					key: `WorkLabelSummary_${renderCheckBox}`,
					validate: { required: false },
					data: { 1: getLabel('h2.am.ShiftingSidePanelComps','onlyWorkingDays') },
					initValue: shiftCountFlag,
					binary: true,
					onUpdate: handleCheckboxUpdate,
				}} />
			</span>
		</div>
		<table className="-shift-side-table">
			<tbody>
				<tr><th>{getLabel('h2.am.ShiftingSidePanelComps','shiftLabel')}</th><th> {getLabel('h2.am.ShiftingSidePanelComps','times')}</th></tr>
				{shiftCountFlag === 1 ?  
					workLabelJsonWeekday && workLabelJsonWeekday.map((val, idx) => <tr key={idx}>
						<td>{adapt(workLabels.find(o => val.id == o.id)?.name)}</td><td>{+val.num || 0} {getLabel('h2.am.ShiftingSidePanelComps','secondary')}{_.isEqual(user.locale, 'en_US') && (+val.num || 0) > 1 ? 's': ''}</td>
					</tr>)
					: 
					workLabelJson && workLabelJson.map((val, idx) => <tr key={idx}>
						<td>{adapt(workLabels.find(o => val.id == o.id)?.name)}</td><td>{+val.num || 0} {getLabel('h2.am.ShiftingSidePanelComps','secondary')}{_.isEqual(user.locale, 'en_US') && (+val.num || 0) > 1 ? 's': ''}</td>
					</tr>)
				}
			</tbody>
		</table>
	</>;
};

export const OverTimeSummary = ({
	workHours,
	otHours,
	unscheduledNum,
	leaveHours,
	leaveAllDayNum,
	link
}) => {

	return (
		<>
			<div className="-desc">
				{getLabel('h2.am.ShiftingSidePanelComps','leaveOvertimeStatistics')}
				{link}
			</div>
			<table className="-shift-side-table">
				<tbody>
					<tr><th>{getLabel('h2.am.ShiftingSidePanelComps','workingHoursStatistics')}</th><th>{getLabel('h2.am.ShiftingSidePanelComps','hoursTimes')}</th></tr>
					{/* <tr><td>上班時數</td><td>{+workHours || 0}小時</td></tr> */}
					<tr><td>{getLabel('h2.am.ShiftingSidePanelComps','overtimeHours')}</td><td>{+otHours || 0} {getLabel('h2.am.ShiftingSidePanelComps','hour')}</td></tr>
					{/* <tr><td>未排班天數</td><td>{+unscheduledNum || 0}天</td></tr> */}
					<tr><td>{getLabel('h2.am.ShiftingSidePanelComps','leaveHours')}</td><td>{+leaveHours || 0} {getLabel('h2.am.ShiftingSidePanelComps','hour')}</td></tr>
					<tr><td>{getLabel('h2.am.ShiftingSidePanelComps','totalFullDayLeaveInstances')}</td><td>{+leaveAllDayNum || 0} {getLabel('h2.am.ShiftingSidePanelComps','secondary')}{_.isEqual(user.locale, 'en_US') && (+leaveAllDayNum || 0) > 1 ? 's': ''}</td></tr>
				</tbody>
			</table>
		</>
	);
};
